import * as Sentry from "@sentry/react";
import qrcodeParser from "qrcode-parser";

function decodeBase64(base64: string): string {
	const binaryString = atob(base64);
	const bytes = new Uint8Array(binaryString.length);
	for (let i = 0; i < binaryString.length; i++) {
		bytes[i] = binaryString.charCodeAt(i);
	}
	return new TextDecoder().decode(bytes);
}

// Narrows the parsed JSON to the `{ data: { id } }` shape the access payload is expected to carry, so a
// BE shape change surfaces as a specific error (below) instead of a generic TypeError on `parsed.data.id`.
const hasAccessId = (value: unknown): value is { data: { id: string } } => {
	if (typeof value !== "object" || value === null || !("data" in value)) {
		return false;
	}
	const data = (value as { data: unknown }).data;
	return typeof data === "object" && data !== null && "id" in data && typeof (data as { id: unknown }).id === "string";
};

/**
 * Decodes the access ID embedded in a QR code base64 image.
 *
 * The QR encodes a URL whose `data` query param is a URI-encoded base64 JSON blob; the access ID is
 * `data.id` inside it. This is the meaningful identity of the access code — two different PNG renders
 * can carry the same access ID, so rotation must be judged on this value, not on the raw image bytes.
 *
 * @param qrCodeBase64 - The base64 encoded QR code image
 * @returns The decoded access ID, or null if decoding fails
 */
export const decodeQRCode = async (qrCodeBase64: string): Promise<string | null> => {
	try {
		const result = await qrcodeParser(qrCodeBase64);
		const url = new URL(result);

		const base64 = url.searchParams.get("data");
		if (!base64) {
			throw new Error(`Failed to decode QR code: no data found in URL: ${url}`);
		}
		const cleanedBase64 = decodeURIComponent(base64);
		const decoded = decodeBase64(cleanedBase64);
		const parsed: unknown = JSON.parse(decoded);

		if (!hasAccessId(parsed)) {
			throw new Error(`Decoded QR payload missing data.id: ${decoded.slice(0, 80)}`);
		}

		return parsed.data.id;
	} catch (error) {
		Sentry.captureException(error);
		return null;
	}
};
